import { Box, Grid, Typography } from '@mui/material';

import { findLast } from 'lodash';

import { ReactComponent as UploadSuccessIcon } from 'common/assets/images/circle-checkmark.svg';
import { ReactComponent as DocTextIcon } from 'common/assets/images/document-img.svg';
import {
	CHAT_TYPE_OPTION,
	CHAT_USER_OPTIONS,
	TIME_STAMP_OPTIONS,
} from 'common/constants/AskRiskLinkConstants';
import { PROGRESS_BAR_STATES } from 'common/constants/ProgressConstants';
import { getFormattedSize } from 'common/utils/utilFunctions';

import {
	IFileInfo,
	IUploadingFile,
	IUploadingFileStatus,
} from 'features/Reporting/api/type-def';
import { IUser } from 'features/Settings/UserManagement/api/type-def';

import {
	IGPTMessageProps,
	IPromptData,
	IResponseGPTMessageData,
} from './api/type-def';

import styles from './OpenRiskGPTUtil.module.scss';

const { DOC } = CHAT_TYPE_OPTION;
const { DATE, TIME } = TIME_STAMP_OPTIONS;
const { USER, BOT } = CHAT_USER_OPTIONS;
const { COMPLETE, INITIAL } = PROGRESS_BAR_STATES;
const { icon } = styles;

export const specifiedDateFormatHandler = (date: string) =>
	new Date(date).toLocaleString('en-US', {
		hour: 'numeric',
		minute: 'numeric',
		hour12: true,
	});

export const getUserName = (firstName: string, lastName: string) =>
	`${firstName} ${lastName}`;

export const formatMessageFromApi = (
	items: IResponseGPTMessageData[],
	loggedInUser: IUser | null,
): IGPTMessageProps[] => {
	const messageArr: IGPTMessageProps[] = [];
	// sort message by create date
	items.sort(
		(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
	);

	const latestUploadedFile = findLast(
		items,
		(item: IResponseGPTMessageData) =>
			item?.component?.toLowerCase() === DOC?.toLowerCase(),
	);
	const latestUploadedFileDate =
		latestUploadedFile === undefined
			? new Date().toDateString()
			: latestUploadedFile?.createdAt;

	items.forEach((item) => {
		const date = new Date(item?.createdAt);
		const formattedDate = date?.toLocaleDateString('en-US');

		if (item?.component?.toLowerCase() === DOC.toLowerCase()) {
			const documentDetails: [] = item.detail as any;
			// to check and render only 1 item if there are multiple uploads
			const index = messageArr?.findIndex(
				(item) => item?.component?.toLowerCase() === DOC.toLowerCase(),
			);

			if (index === -1) {
				const userMsg: IGPTMessageProps = {
					type: USER,
					name: loggedInUser
						? getUserName(loggedInUser?.firstName, loggedInUser?.lastName)
						: '',
					time: specifiedDateFormatHandler(latestUploadedFileDate),
					date: new Date(latestUploadedFileDate).toLocaleDateString('en-US'),
					message: '',
					documents: documentDetails,
					isUploadStatus: true,
					component: DOC,
				};
				messageArr.push(userMsg);
			} else {
				messageArr[index].documents?.push(...documentDetails);
			}
		} else {
			const userMsg: IGPTMessageProps = {
				type: USER,
				name: loggedInUser
					? getUserName(loggedInUser?.firstName, loggedInUser?.lastName)
					: '',
				time: specifiedDateFormatHandler(item.createdAt),
				date: formattedDate,
				uniqueId: item.id,
				messageId: item.messageId,
				feedback: item.detail.feedback,
				message: item.detail.prompt,
				component: item?.component,
				sources: item?.detail?.sources,
			};
			messageArr.push(userMsg);
			const botMsg: IGPTMessageProps = {
				type: BOT,
				name: 'Ask Risk Link',
				time: specifiedDateFormatHandler(item.createdAt),
				date: formattedDate,
				message: item.detail.response,
				feedback: item.detail.feedback,
				uniqueId: item.id,
				messageId: item.messageId,
				prompt: item.detail.prompt,
				component: item?.component,
				sources: item?.detail?.sources,
				promptRole: item?.detail?.prompt_role,
			};
			messageArr.push(botMsg);
		}
	});
	// sort message again for the uploaded history bubble
	messageArr.sort(
		(a, b) =>
			new Date(a.time + a.date).getTime() - new Date(b.time + b.date).getTime(),
	);
	return messageArr;
};

export enum OPEN_RISK_GPT {
	CHAT_NAME = 'Risk Link Chat',
	ASK_RISK_LINK = 'Ask Risk Link',
	CHAT_SESSION_ERROR_MESSAGE = 'Unable to create chat session',
	CHAT_SESSION_UNABLE_RETRIEVE_MESSAGE = 'Unable to retrieve session detail',
	WELCOME_MESSAGE = "Let's start here",
	CHAT_NAME_UPDATE_SUCCESS_MESSAGE = 'Chat name updated successfully',
	CHAT_NAME_UPDATE_FAILURE_MESSAGE = 'Unable to update chat name',
	CHAT_HISTORY_RETRIVE_FAILURE_MESSAGE = 'Unable to retrive chat history',
	RESPONSE_FAILURE_MESSAGE = 'Unable to get response',
	NETWORK_ISSUE_MESSAGE = 'Getting network issue now',
	CHAT_TITLE_FAILURE_MESSAGE = 'Unable to get chat title',
	NEW_CHAT = 'New Chat',
	REL_INFO_MESSAGE = 'Get quick and reliable information by Ask Risk Link!',
	DISCALIMER_MESSAGE = 'Disclaimer: The content provided may not always be accurate, and it is advisable to verify the facts with human oversight before making any decisions.',
}

/**
 * @description to set the cursor for the prompt
 */
export const cursorSetterHandler = (id: string, cursorPosition: number) => {
	const inputElement = document.getElementById(
		'ask-risk-link-input',
	) as HTMLInputElement;
	if (inputElement && cursorPosition) {
		inputElement.focus();
		inputElement.setSelectionRange(cursorPosition, cursorPosition);
	}
};

/**
 * @description to current time stamp
 * @returns result
 */
export const currentTimeStamp = (type: string) => {
	let result = '';

	if (type?.toLowerCase() === DATE?.toLowerCase()) {
		result = new Date().toLocaleDateString('en-US', {
			month: 'numeric',
			day: '2-digit',
			year: 'numeric',
		});
	} else {
		result = new Date().toLocaleString('en-US', {
			hour: 'numeric',
			minute: 'numeric',
			hour12: true,
		});
	}

	return result;
};

export const onPromptSelect = (
	data: IPromptData,
	setCursorPosition: (value: any) => void,
	setSearchVal: (value: string, role: string) => void,
) => {
	if (data.prompt) {
		const replaceBoldTags = data.prompt
			?.replace(/<b>/g, '')
			?.replace(/<\/b>/g, '');
		setCursorPosition(replaceBoldTags?.length);
		setSearchVal(
			replaceBoldTags,
			data?.configuration?.role[0] ? data.configuration.role[0] : '',
		);
	}
};

export const hasDocComponent = (messages: IGPTMessageProps[]) =>
	messages?.some(
		(item) => item?.component?.toLowerCase() === DOC?.toLowerCase(),
	);

export const getMessageWithReUpload = (
	messages: IGPTMessageProps[],
	uploadStatus: IUploadingFileStatus | null,
	loggedInUser: IUser | null,
): IGPTMessageProps[] => {
	const messageWithoutReUpload = messages.filter(
		(message: IGPTMessageProps) => Boolean(message.isReUpload) === false,
	);
	if (!uploadStatus) return messageWithoutReUpload;
	const { uploadSuccessFiles, uploadFailedFiles, reuploadSuccessFiles } =
		uploadStatus;
	const hasNoReUploadFile =
		uploadSuccessFiles.length +
			uploadFailedFiles.length +
			reuploadSuccessFiles.length ===
		0;
	if (hasNoReUploadFile) return messageWithoutReUpload;
	const date = new Date(uploadStatus?.timeStamp);
	const formattedDate = date?.toLocaleDateString('en-US');
	const uploadObject: IGPTMessageProps = {
		type: USER,
		name: loggedInUser
			? getUserName(loggedInUser?.firstName, loggedInUser?.lastName)
			: '',
		time: specifiedDateFormatHandler(uploadStatus?.timeStamp),
		date: formattedDate,
		message: '',
		isReUpload: true,
		component: DOC,
	};
	// replace re-upload history with latest one
	const result = [
		...messages.filter(
			(message: IGPTMessageProps) => Boolean(message.isReUpload) === false,
		),
		uploadObject,
	];
	// sort message by date
	result.sort(
		(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
	);
	return result;
};

/**
 * @description to set the progress to 100 when file upload is completed
 */
export const fileUploadCompleteHandler = (files: any) => {
	files?.forEach((file: any) => {
		file.progress = COMPLETE;
	});
};

/**
 * @description to set the progress to 0 when file upload is started
 */
export const fileUploadStartHandler = (uploadFailedFiles: IUploadingFile[]) => {
	const updatedArray = uploadFailedFiles?.map((obj) => ({
		...obj,
		progress: INITIAL,
	}));
	return updatedArray;
};

/**
 * @description to update the failed file data
 * @returns data with progress key value pair
 */
export const updatedFailedFileData = (
	uploadFailedFiles: IUploadingFile[],
	uploadProgress: any,
) => {
	const updatedUploadFailedFiles = uploadFailedFiles?.map((obj, index) => {
		if (index === uploadProgress?.index) {
			return {
				...obj,
				progress: uploadProgress?.progress,
			};
		}
		return obj;
	});
	return updatedUploadFailedFiles;
};

/**
 * @description to remove the uploaded file from failed upload list
 * @returns new ReUploadHistory and new upload FailedFiles
 */
export const getRefreshedReUploadHistory = (
	uploadedFile: any[],
	reUploadHistory: IUploadingFileStatus | null,
) => {
	// remove the uploaded file from failed upload list
	let failedFileArr: IUploadingFile[] =
		reUploadHistory === null ? [] : [...reUploadHistory.uploadFailedFiles];
	if (failedFileArr.length > 0) {
		uploadedFile.forEach((uploadedSucFile: any) => {
			failedFileArr = failedFileArr.filter(
				(failedFile) => uploadedSucFile.name !== failedFile.name,
			);
		});
	}
	// reset the progress of the failed file upload
	const updatedArray = fileUploadStartHandler(failedFileArr);
	const newReUploadHistory: IUploadingFileStatus = {
		timeStamp: String(new Date()),
		uploadSuccessFiles: [],
		uploadFailedFiles: updatedArray,
		reuploadSuccessFiles: [],
	};

	return {
		failedFileArr: updatedArray,
		newReUploadHistory: failedFileArr.length > 0 ? newReUploadHistory : null,
	};
};

export const getMessageWithUploadedFiles = (
	messages: IGPTMessageProps[],
	loggedInUser: IUser | null,
): IGPTMessageProps[] => {
	const message: IGPTMessageProps = {
		type: USER,
		name: loggedInUser
			? getUserName(loggedInUser?.firstName, loggedInUser?.lastName)
			: '',
		time: currentTimeStamp(TIME),
		date: currentTimeStamp(DATE),
		message: '',
		isUploadStatus: true,
		component: DOC,
	};

	// replace uploaded files with latest one
	const result = [
		...messages.filter(
			(message: IGPTMessageProps) => Boolean(message.isUploadStatus) === false,
		),
		message,
	];

	return result;
};

/**
 * @description to get re-upload success file from uploading file list
 * @returns re-upload success file list
 */
export const getReUploadSuccessFile = (
	uploadingFiles: IUploadingFile[],
	uploadedFiles: IFileInfo[],
) => {
	const reuploadSuccessFiles = [] as IFileInfo[];
	uploadedFiles.forEach((uploadedFile) => {
		const res = uploadingFiles.filter(
			(uploadingFile) => uploadingFile.name === uploadedFile.name,
		);
		if (res.length > 0) reuploadSuccessFiles.push(uploadedFile);
	});
	return reuploadSuccessFiles;
};

export const FILE_UPLOADING_STATUS = 'uploading';
export const FILE_UPLOADING_FINISHED = 'finished';

export const filterUploadFinishedFiles = (items: any) =>
	items.filter(
		(doc: any) => doc?.status.toLowerCase() !== FILE_UPLOADING_STATUS,
	);

export const filterDocsFromUploadedMessages = (data: any) => {
	const filteredData = data.reduce((acc: any, item: any) => {
		if (item?.component === 'doc') {
			const finishedDetails = item?.detail.filter(
				(detail: any) =>
					detail?.status?.toLowerCase() === FILE_UPLOADING_FINISHED,
			);
			if (finishedDetails?.length > 0) {
				acc.push({ ...item, detail: finishedDetails });
			}
		} else {
			acc.push(item);
		}
		return acc;
	}, []);
	return filteredData;
};
export const getFailedUploadedFileNames = (
	uploadFailedFiles: IUploadingFile[],
) => {
	let failedFileNames = [];
	if (uploadFailedFiles?.length > 0) {
		failedFileNames = uploadFailedFiles.map((ele: any) => ele?.name);
	}
	return failedFileNames;
};

export const RiskLinkUploadedDocument = ({ file }: any) => (
	<Grid container className={styles['upload-record']} key={file?.name}>
		<Grid container className={styles['upload-record-grid']}>
			<Grid item>
				<Typography className={styles['record-content']}>
					<DocTextIcon className={icon} />
					<Typography className={styles['file-name']}>{file?.name}</Typography>
					<Typography className={styles['file-size']}>
						{getFormattedSize(file?.size)}
					</Typography>
				</Typography>
			</Grid>
			<Grid item>
				<Box className={styles['record-content']}>
					{file?.status === 'finished' && (
						<UploadSuccessIcon className={icon} />
					)}
				</Box>
			</Grid>
		</Grid>
	</Grid>
);
